/**
 * THE REFERENCE LAYER, bound to this app.
 *
 * `lib/referenceEngine.js` is the pure selection + licensing logic and
 * `data/reference_registry.json` is the precedent index. THIS file is the only
 * place the two meet: it loads the registry and partially applies the engine to
 * it — exactly the pattern `features/command-center/assignments.js` uses for
 * `assignmentEngine.js` + `rep_proficiency.json`.
 *
 * The registry is read at RUNTIME, so growing the corpus (DURABLE-PIPELINE.md
 * §3.2A) sharpens every selection by re-bundling that JSON alone. No code here
 * changes.
 *
 * IT PROPOSES; IT NEVER WRITES. Nothing in this module or its callers puts a
 * precedent into the render payload — the payload is `buildPayload.js` and it
 * is unchanged. The precedent is shown to the operator, who decides what to
 * take from it. Recording the chosen precedent ON the build record is backend
 * work and is specced, not invented (see ARTWORK_ENGINE.md).
 */

import REGISTRY from '../../data/reference_registry.json'
import {
  CONSTRUCTION,
  IDENTITY,
  houseLookSet,
  resolveReference as rawResolve,
  rungNote,
  selectPerJob,
  verificationNote,
} from '../../lib/referenceEngine.js'

export { CONSTRUCTION, IDENTITY, rungNote, verificationNote }
export { REGISTRY as REFERENCE_REGISTRY }

/** Every reference in the index, active or not — the count the panel reports. */
export const REFERENCE_COUNT = Array.isArray(REGISTRY.references) ? REGISTRY.references.length : 0

/** The six house-look references attached to every job. */
export const HOUSE_SET = houseLookSet(REGISTRY)

/** Resolve one reference to its durable address + fetch plan. */
export function resolveReference(referenceId, opts) {
  return rawResolve(REGISTRY, referenceId, opts)
}

/** The precedent for a job, with the reason and the alternates. */
export function selectPrecedent(job, opts) {
  return selectPerJob(REGISTRY, job, opts)
}

/**
 * THE STUDIO'S BUILD STATE → the engine's job shape.
 *
 * Deliberately narrow. The sport comes from what the operator actually set
 * (the Setting & Output sport) or from the championship record; the level from
 * the championship record. NOTHING is guessed: a field with no source stays
 * empty, and an empty field simply scores zero rather than matching everything.
 */
export function jobFromBuild(build, { championship = null, school = null } = {}) {
  const s = (v) => (v == null ? '' : String(v).trim())
  const ch = championship || {}
  const sc = school || {}
  return {
    sport: s((build && build.setting && build.setting.sport) || ch.sport),
    level: s(ch.level || ch.sr_level),
    // The studio builds products; the registry's `asset_type` is 'concept' for
    // a generated piece and 'presentation' for a delivered print. A render is a
    // concept until somebody advances it, which is the studio's own default.
    assetType: 'concept',
    metal: s(build && build.components && build.components.metal && build.components.metal.tone),
    customer: s(sc.name || ch.school || ch.champion || ch.current_champion),
  }
}
